import React, {useState, useEffect} from 'react';
import {Button} from 'antd';
import {
    CustomerServiceOutlined,
    MailOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    PhoneOutlined,
    UserOutlined,
    WechatOutlined
} from '@ant-design/icons';
import {useLanguage} from '../contexts/LanguageContext';
import {useSidebar} from "../contexts/SidebarContext";
import {doc, getDoc} from 'firebase/firestore';
import {db} from '../config/firebase';

interface SalesContactData {
    phone: string;
    email: string;
    director: string;
    wechat: string;
}

const defaultValues = {
    phone: "792-238-7818",
    email: "xcyvegas888@gmail.com",
    director: "Jessy Xu",
    wechat: "LXExhibition"
};

const Sidebar: React.FC = () => {
    const {openSidebar, closeSidebar, isOpen, isShaking} = useSidebar();
    const {currentLang} = useLanguage();
    const [contactInfo, setContactInfo] = useState<SalesContactData>(defaultValues);

    useEffect(() => {
        fetchSalesContact();
    }, []);

    const fetchSalesContact = async () => {
        try {
            const docRef = doc(db, 'config', 'salesContact');
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                setContactInfo(docSnap.data() as SalesContactData);
            }
        } catch (error) {
            console.error('Error fetching sales contact:', error);
            // 如果读取失败，保持默认值
        }
    };

    const getText = (key: string) => {
        const textContent = {
            salesContact: {
                en: 'Sales Contact',
                zh: '销售联系方式'
            },
            salesDirector: {
                en: 'Sales Director',
                zh: '销售部总监'
            },
            salesPhone: {
                en: 'Sales Phone',
                zh: '销售电话'
            },
            salesEmail: {
                en: 'Sales Email',
                zh: '销售邮箱'
            },
            wechat: {
                en: 'Sales Wechat',
                zh: '销售微信'
            }
        };

        return textContent[key as keyof typeof textContent][currentLang];
    };

    const handlePhoneClick = () => {
        window.location.href = `tel:${contactInfo.phone}`;
    };

    const handleEmailClick = () => {
        window.location.href = `mailto:${contactInfo.email}`;
    };

    return (
        <div className={`fixed right-0 top-1/2 -translate-y-1/2 transition-all duration-300 
        ${isOpen ? 'w-64' : 'w-12'} bg-white shadow-lg rounded-l-lg z-50
        ${isShaking ? 'animate-shake' : ''}`}>
            {isOpen ? (
                <div>
                    <div>
                        <Button
                            type="text"
                            className="absolute left-0 top-0 z-10"
                            icon={<MenuUnfoldOutlined/>}
                            onClick={() => closeSidebar()}
                        />

                        {/* 标题 */}
                        <div className="text-orange-500 font-bold text-lg pt-1">
                            {getText('salesContact')}
                        </div>
                    </div>

                    <div className="p-4 pt-6">
                        <div className="space-y-4"> {/* 增加整体间距 */}
                            {/* 销售总监 */}
                            <div className="space-y-1"> {/* 使用space-y-1控制标题和内容的间距 */}
                                <div className="flex items-center">
                                    <UserOutlined className="mr-2"/>
                                    <span>{getText('salesDirector')}</span>
                                </div>
                                <div className="pl-6 text-orange-500">{contactInfo.director}</div>
                            </div>

                            {/* 电话 */}
                            <div className="space-y-1">
                                <div className="flex items-center">
                                    <PhoneOutlined className="mr-2"/>
                                    <span>{getText('salesPhone')}</span>
                                </div>
                                <div
                                    className="pl-6 text-orange-500 cursor-pointer hover:underline"
                                    onClick={handlePhoneClick}
                                >
                                    {contactInfo.phone}
                                </div>
                            </div>

                            {/* 邮箱 */}
                            <div className="space-y-1">
                                <div className="flex items-center">
                                    <MailOutlined className="mr-2"/>
                                    <span>{getText('salesEmail')}</span>
                                </div>
                                <div
                                    className="pl-6 text-orange-500 cursor-pointer hover:underline"
                                    onClick={handleEmailClick}
                                >
                                    {contactInfo.email}
                                </div>
                            </div>

                            {/* 微信 */}
                            <div className="space-y-1">
                                <div className="flex items-center">
                                    <WechatOutlined className="mr-2"/>
                                    <span>{getText('wechat')}</span>
                                </div>
                                <div className="pl-6 text-orange-500">
                                    {contactInfo.wechat}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            ) : (
                <div
                    className="h-20 flex flex-col items-center justify-center cursor-pointer"
                    onClick={openSidebar}>
                    <CustomerServiceOutlined className="text-2xl text-orange-500 mb-2"/>
                    <MenuFoldOutlined className="text-gray-500"/>
                </div>
            )}
        </div>
    );
};

export default Sidebar;
